import React, { useEffect, useMemo, useRef } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import {
  createGame, applyAction, getPlayerView, DEFAULT_CONFIG,
  type DFState, type DFConfig, type DFAction, type DFPlayerViewV2, type DFPlayerViewV1,
  type DFStateV3, type DFActionV3,
} from '../../../games/dice-factory/dice-factory';
import type { RoomView } from '../../../types/room';

// Legacy UI components — we keep them as-is and feed them a fake socket
import { DiceFactoryGame as DFGameV2 } from '../dice-factory-legacy/v0.2.1';
import { DiceFactoryGame as DFGameV1 } from '../dice-factory-legacy/v0.1.5';

// v0.3 UI
import DiceFactoryV3 from './v0.3/DiceFactoryV3';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: DFState | null;
  onFinish: () => void;
}

// Creates a fake socket.io-like object that routes game actions to the relay.
// The legacy UI calls socket.emit(eventName, data) for every action.
// We intercept those and dispatch them as DFAction.
function createFakeSocket(
  dispatch: (action: DFAction) => void,
  myId: string,
  getState: () => DFState | undefined,
) {
  const listeners: Record<string, Array<(data: unknown) => void>> = {};

  const fake = {
    id: myId,
    connected: true,

    emit(event: string, data: Record<string, unknown>) {
      const state = getState();
      if (!state) return;

      if (state.version === 'v0.2.1') {
        switch (event) {
          case 'dice-factory:execute-ability':
            dispatch({
              type: 'execute_ability',
              playerId: myId,
              abilityId: data.abilityId as string,
              slotNumber: data.slotNumber as number,
              costDiceIds: (data.costDiceIds as string[]) ?? [],
              costCardIds: (data.costCardIds as string[]) ?? [],
              targetDieId: data.targetDieId as string | string[] | undefined,
              targetValue: data.targetValue as number | undefined,
              bumpDirection: (data.bumpDirection as 'up' | 'down') ?? 'up',
              bumpAmount: (data.bumpAmount as number) ?? 1,
            });
            break;
          case 'dice-factory:buy-card':
            dispatch({
              type: 'buy_card',
              playerId: myId,
              cardId: data.cardId as string,
              costDiceIds: (data.costDiceIds as string[]) ?? [],
            });
            break;
          case 'dice-factory:assign-slot':
            dispatch({
              type: 'assign_slot',
              playerId: myId,
              slotNumber: data.slotNumber as number,
              abilityId: (data.abilityId as string | null) ?? null,
            });
            break;
          case 'dice-factory:pass-turn':
            dispatch({ type: 'pass_turn', playerId: myId });
            break;
          case 'dice-factory:respond-to-attack':
            dispatch({
              type: 'respond_to_attack',
              playerId: myId,
              attackId: data.attackId as string,
              diceIds: (data.diceIds as string[]) ?? [],
            });
            break;
          // swap_slots, clear_slot, card_unexhaust, card_cost_reduction,
          // swap_plus, mass_unexhaust — legacy UI triggers these via execute-ability
          // with a special slotNumber. No-op here; handled via execute_ability above.
          case 'dice-factory:use-card-dice':
            // Client-side UI management only in relay model; ignore.
            break;
          default:
            break;
        }
      } else {
        // v0.1.5 events
        switch (event) {
          case 'dice-factory:recruit':
            dispatch({ type: 'recruit_dice', playerId: myId, diceIds: (data.diceIds as string[]) ?? [] });
            break;
          case 'dice-factory:promote':
            dispatch({ type: 'promote_dice', playerId: myId, diceIds: (data.diceIds as string[]) ?? [] });
            break;
          case 'dice-factory:process':
            dispatch({ type: 'process_dice', playerId: myId, diceIds: (data.diceIds as string[]) ?? [] });
            break;
          case 'dice-factory:pip-action':
            if (data.actionType === 'reroll') {
              dispatch({ type: 'reroll_die', playerId: myId, dieId: (data.diceIds as string[])[0] });
            } else {
              dispatch({
                type: 'modify_die', playerId: myId,
                dieId: (data.diceIds as string[])[0],
                change: data.actionType === 'increase' ? 1 : -1,
              });
            }
            break;
          case 'dice-factory:score-straight':
            dispatch({ type: 'score_straight', playerId: myId, diceIds: (data.diceIds as string[]) ?? [] });
            break;
          case 'dice-factory:score-set':
            dispatch({ type: 'score_set', playerId: myId, diceIds: (data.diceIds as string[]) ?? [] });
            break;
          case 'dice-factory:place-bid':
            dispatch({
              type: 'place_bid', playerId: myId,
              modificationId: data.modificationId as string,
              bidAmount: data.bidAmount as number,
            });
            break;
          case 'dice-factory:end-turn':
            dispatch({ type: 'end_turn', playerId: myId });
            break;
          default:
            break;
        }
      }
    },

    on(event: string, cb: (data: unknown) => void) {
      if (!listeners[event]) listeners[event] = [];
      listeners[event].push(cb);
    },

    off(event: string, cb: (data: unknown) => void) {
      if (listeners[event]) {
        listeners[event] = listeners[event].filter(fn => fn !== cb);
      }
    },

    _emit(event: string, data: unknown) {
      (listeners[event] ?? []).forEach(fn => fn(data));
    },
  };

  return fake;
}

export default function DiceFactoryRoomGame({ slug, room, myId, isLeader, initialState, onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<DFState>(slug);

  useEffect(() => {
    if (!isLeader || !initialState) return;
    startRelay('dice-factory', initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stateRef = useRef<DFState | undefined>(undefined);
  stateRef.current = state ?? undefined;

  const fakeSocket = useMemo(() => {
    return createFakeSocket(
      (action: DFAction) => {
        const s = stateRef.current;
        if (!s) return;
        const newState = applyAction(s, action);
        sendAction(action, newState);
      },
      myId,
      () => stateRef.current,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId]);

  // Notify the UI about incoming attacks by synthesizing a fake socket event
  const prevPendingAttacksRef = useRef<string[]>([]);
  useEffect(() => {
    if (!state || state.version !== 'v0.2.1') return;
    const v2State = state as import('../../../games/dice-factory/dice-factory').DFStateV2;
    const attackIds = v2State.pendingAttacks.map(a => a.id);
    const prevIds = prevPendingAttacksRef.current;
    for (const atk of v2State.pendingAttacks) {
      if (!prevIds.includes(atk.id) && atk.waitingFor.includes(myId)) {
        fakeSocket._emit('dice-factory:attack-request', {
          attackId: atk.id,
          attackerName: atk.attackerName,
          diceCount: atk.diceCount,
          type: atk.type,
        });
      }
    }
    prevPendingAttacksRef.current = attackIds;
  }, [state, myId, fakeSocket]);

  const playerView = useMemo(() => {
    if (!state) return null;
    return getPlayerView(state, myId);
  }, [state, myId]);

  if (!playerView) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#111', color: '#fff' }}>
        Loading Dice Factory...
      </div>
    );
  }

  if (playerView.version === 'v0.3') {
    const dispatchV3 = (action: DFActionV3) => {
      const s = stateRef.current as DFStateV3 | undefined;
      if (!s) return;
      const newState = applyAction(s, action as DFAction);
      sendAction(action as DFAction, newState);
    };
    return <DiceFactoryV3 state={playerView as DFStateV3} myId={myId} dispatch={dispatchV3} />;
  }

  if (playerView.version === 'v0.1.5') {
    return (
      <DFGameV1
        gameState={playerView as unknown as any}
        socket={fakeSocket as unknown as any}
        isLeader={isLeader}
      />
    );
  }

  return (
    <DFGameV2
      gameState={playerView as unknown as any}
      socket={fakeSocket as unknown as any}
      isLeader={isLeader}
    />
  );
}

export { createGame as createDiceFactoryGame, DEFAULT_CONFIG as DICE_FACTORY_DEFAULT_CONFIG };
export type { DFState as DiceFactoryState, DFConfig as DiceFactoryConfig };
